import React, { useState, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet';
import { Link, useNavigate } from 'react-router-dom';
import { Alert, Button, Grid, CircularProgress, Chip, TextField, Box } from '@mui/material';

import { useLazyQuery } from '@apollo/client';
import i18next from 'i18next';
import AddIcon from '@mui/icons-material/Add';
import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccount';
import PersonIcon from '@mui/icons-material/Person';
import BlockIcon from '@mui/icons-material/Block';
import SearchIcon from '@mui/icons-material/Search';
import { debounce } from 'lodash';

import PagedTable from 'components/table/PagedTable';
import { FormatFromNow } from 'components/DateTime';
import { filterSelector } from 'helper/filter';
import { userSelector, hasAnyAdminSpace } from 'helper/security';

import { USERS_LIST_QUERY } from '../gql';

import { ListUsersQuery } from '__generated__/graphql';
import { ErrorAlert } from 'pages/error';
type TListUsersElement = ListUsersQuery['listUsers']['users'][0];

/**
 * Users list page with pagination and filtering by space.
 * Shows user details including name, email, roles, and spaces.
 */
export default function UsersList() {
  const navigate = useNavigate();
  const filter = filterSelector();
  const user = userSelector()!;
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');

  const [query, { loading, error, data }] = useLazyQuery(USERS_LIST_QUERY, {
    fetchPolicy: 'network-only',
  });

  const debouncedUpdate = useCallback(
    debounce((value: string) => {
      setDebouncedSearchTerm(value);
    }, 500),
    []
  );

  useEffect(() => {
    debouncedUpdate(searchTerm);
  }, [searchTerm, debouncedUpdate]);

  return (
    <>
      <Helmet>
        <title>{i18next.t('users-list-page-title')}</title>
      </Helmet>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, marginBottom: 2 }}>
            <SearchIcon />
            <TextField
              label={i18next.t('search')}
              variant="outlined"
              size="small"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              placeholder={String(i18next.t('users-search-placeholder'))}
              sx={{
                minWidth: 300,
                '& .MuiInputBase-input': { py: 0.5 },
              }}
            />
          </Box>
          <PagedTable
            filterKey={`${JSON.stringify(filter)}-${debouncedSearchTerm}`}
            refetchPage={(skip, take) =>
              query({
                variables: {
                  spaceId: (filter && filter.spaceId) || null,
                  skip,
                  take,
                  search: debouncedSearchTerm.length >= 2 ? debouncedSearchTerm : null,
                },
              })
            }
            rowsLoading={loading}
            rowsErr={error}
            totalCount={data?.listUsers.totalCount}
            headers={[
              i18next.t('users-list-header-name'),
              i18next.t('users-list-header-username'),
              i18next.t('users-list-header-email'),
              ...(user.isSingleSpace ? [i18next.t('users-list-header-isadmin')] : [i18next.t('list-header-space')]),
              i18next.t('users-list-header-islocked'),
              i18next.t('users-list-header-lastseen'),
              ...(user.isSingleSpace ? [] : [i18next.t('users-list-header-memberships')]),
            ]}
            rows={(data?.listUsers.users || []).map(row => [
              <Link to={`/settings/users/${row.id}`}>{row.name}</Link>,
              row.username,
              row.email,
              ...(user.isSingleSpace
                ? [row.isAdmin ? <SupervisorAccountIcon /> : null]
                : [
                    <Chip
                      icon={row.isAdmin ? <SupervisorAccountIcon /> : <PersonIcon />}
                      label={row.space?.name || i18next.t('space-seminargo')}
                      {...(row.space ? { onClick: () => navigate(`/settings/spaces/${row.space!.id}`) } : {})}
                    />,
                  ]),
              row.isLocked && <BlockIcon />,
              <FormatFromNow date={row.lastSeen} />,
              ...(user.isSingleSpace
                ? []
                : [
                    row.memberships
                      .filter(m => m.space?.id !== row.space?.id)
                      .map((m, i) => (
                        <Chip
                          key={i}
                          icon={m.isAdmin ? <SupervisorAccountIcon /> : <PersonIcon />}
                          label={m.space?.name}
                          onClick={() => navigate(`/settings/spaces/${m.space?.id}`)}
                        />
                      )),
                  ]),
            ])}
            defaultPageSize={10}
          />
        </Grid>
        {hasAnyAdminSpace(user) && (
          <Grid item xs={12}>
            <Button sx={{ marginRight: 2 }} variant="contained" startIcon={<AddIcon />} onClick={() => navigate('/settings/users/_create')}>
              {i18next.t('users-list-add')}
            </Button>
          </Grid>
        )}
      </Grid>
    </>
  );
}
